package fr.labodoc.healthProfessional.webApp.pages.laboratories

import fr.labodoc.healthProfessional.webApp.App
import fr.labodoc.healthProfessional.webApp.components.hr
import fr.labodoc.healthProfessional.webApp.components.labodocSpinner
import fr.labodoc.healthProfessional.webApp.data.model.LaboratoryModel
import fr.labodoc.healthProfessional.webApp.data.repository.LaboratoriesRepository
import fr.labodoc.require
import io.kvision.core.Container
import io.kvision.core.onChange
import io.kvision.form.check.checkBox
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.state.*
import io.kvision.toast.Toast
import kotlinx.coroutines.launch
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject

class LaboratoriesPage : SimplePanel() {
  private interface ViewModel {
    sealed interface Filter {
      data object All : Filter

      data object Favorites : Filter
    }

    sealed interface LaboratoriesState {
      data object Loading : LaboratoriesState

      data object Error : LaboratoriesState

      data class Loaded(
        val laboratories: ObservableList<LaboratoryModel>
      ) : LaboratoriesState
    }

    val filter: ObservableState<Filter>
    val laboratoriesState: ObservableState<LaboratoriesState>

    fun filterLaboratories(filter: Filter)
    fun setLaboratoryFavoriteStatus(laboratory: LaboratoryModel, favoriteStatus: Boolean)
  }

  private class ViewModelImpl : ViewModel, KoinComponent {
    private val laboratoryRepository: LaboratoriesRepository by inject()

    private val laboratories: MutableList<LaboratoryModel> =
      mutableListOf()

    private val filteredLaboratories: ObservableListWrapper<LaboratoryModel> =
      ObservableListWrapper()

    override val filter: ObservableValue<ViewModel.Filter> =
      ObservableValue(ViewModel.Filter.All)

    override val laboratoriesState: ObservableValue<ViewModel.LaboratoriesState> by lazy {
      val observableValue: ObservableValue<ViewModel.LaboratoriesState> =
        ObservableValue(ViewModel.LaboratoriesState.Loading)

      App.Companion.scope.launch {
        laboratoryRepository
          .getLaboratories()
          .onLeft {
            laboratoriesState.setState(ViewModel.LaboratoriesState.Error)
          }
          .onRight {
            laboratories.addAll(it)
            filteredLaboratories.addAll(laboratories)
            laboratoriesState.setState(ViewModel.LaboratoriesState.Loaded(filteredLaboratories))
          }
      }

      observableValue
    }

    private fun updateLaboratory(laboratoryId: String, block: (LaboratoryModel) -> LaboratoryModel) {
      laboratories.apply {
        find { it.id == laboratoryId }?.let { laboratory ->
          set(indexOf(laboratory), block(laboratory))
        }
      }
    }

    override fun filterLaboratories(filter: ViewModel.Filter) {
      App.Companion.scope.launch {
        this@ViewModelImpl.filter.setState(filter)

        filteredLaboratories.clear()
        when (filter) {
          ViewModel.Filter.All -> filteredLaboratories.addAll(laboratories)
          ViewModel.Filter.Favorites -> filteredLaboratories.addAll(laboratories.filter { it.favorite })
        }
      }
    }

    override fun setLaboratoryFavoriteStatus(laboratory: LaboratoryModel, favoriteStatus: Boolean) {
      App.Companion.scope.launch {
        val response = if (favoriteStatus)
          laboratoryRepository.favorite(laboratory.id)
        else
          laboratoryRepository.unfavorite(laboratory.id)

        response
          .onLeft { Toast.danger("Une erreur est survenue lors de la mise à jour du favori") }
          .onRight { updateLaboratory(laboratory.id) { it.copy(favorite = favoriteStatus) } }
      }
    }
  }

  private val viewModel: ViewModel = ViewModelImpl()

  init {
    id = "page-laboratories"
    require("./css/pages/laboratories/laboratories.css")

    div(className = "page-width") {
      header {
        div(className = "title") {
          h1 {
            content = "Les Laboratoires"
          }
        }

        div(className = "filters") {
          div(className = "buttons") {
            button("Tous") {
              onClick { viewModel.filterLaboratories(ViewModel.Filter.All) }
            }.bind(viewModel.filter) { filter ->
              if (filter is ViewModel.Filter.All) addCssClass("active") else removeCssClass("active")
            }

            button("Favoris") {
              onClick { viewModel.filterLaboratories(ViewModel.Filter.Favorites) }
            }.bind(viewModel.filter) { filter ->
              if (filter is ViewModel.Filter.Favorites) addCssClass("active") else removeCssClass("active")
            }
          }
        }
      }

      div(className = "container").bind(viewModel.laboratoriesState) { uiState ->
        when (uiState) {
          ViewModel.LaboratoriesState.Loading -> {
            labodocSpinner()
          }

          ViewModel.LaboratoriesState.Error -> {
            p("Erreur")
          }

          is ViewModel.LaboratoriesState.Loaded -> {
            div(className = "cards").bindEach(uiState.laboratories) { laboratory ->
              link(label = "", url = laboratory.websiteUrl, target = "_blank") {
                div(className = "card") {
                  header {
                    image(laboratory.logoUrl, laboratory.name)

                    checkBox {
                      addCssClass("favorite")
                      value = laboratory.favorite

                      onChange {
                        viewModel.setLaboratoryFavoriteStatus(laboratory, value)
                      }

                      onClick { it.stopPropagation() }
                    }
                  }

                  hr()

                  p {
                    content = laboratory.name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

fun Container.laboratoriesPage(): LaboratoriesPage {
  val laboratoriesPage = LaboratoriesPage()

  this.add(laboratoriesPage)
  return laboratoriesPage
}
